import { forwardRef, useEffect, useImperativeHandle } from "react"
import { Trans, useTranslation } from "react-i18next"
import {
  TextInput,
  Textarea,
  Button,
  Group,
  Text,
  Select,
  MultiSelect,
  Rating,
  Paper,
  Switch,
  Stack,
} from "@mantine/core"
import { DateInput } from "@mantine/dates"
import { useForm } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import type { AgencyVisitDetail, AgencyVisitFormValues } from "../models/agencyVisit"
import { useCreateAgencyVisit, useUpdateAgencyVisit, useVisitUsers, useVisitAgencies } from "../hooks/useAgencyVisits"
import { FormSection } from "../../../components/crud"

interface Props {
  visit?: AgencyVisitDetail
  onSuccess: () => void
}

const AgencyVisitFormContent = forwardRef<{ submit: () => void }, Props>(({ visit, onSuccess }, ref) => {
  const { t } = useTranslation()
  const isEdit = !!visit
  const { data: users } = useVisitUsers()
  const { data: agencies } = useVisitAgencies()
  const createMutation = useCreateAgencyVisit()
  const updateMutation = useUpdateAgencyVisit()

  const form = useForm<AgencyVisitFormValues>({
    initialValues: {
      agency_id: 0,
      date: "",
      reason: "",
      status: 5,
      rating: null,
      agency_appearance: null,
      technical_aspects: null,
      human_aspects: null,
      comercial_aspects: null,
      summary: null,
      next_steps: null,
      internal_notes: null,
      users: [],
      notification_users: [],
    },
    validate: {
      agency_id: (v) => (v ? null : t("agencyVisits.validation.agencyRequired")),
      date: (v) => (v ? null : t("agencyVisits.validation.dateRequired")),
      reason: (v) => (v?.length ? null : t("agencyVisits.validation.reasonRequired")),
    },
  })

  useEffect(() => {
    if (visit) {
      form.setValues({
        agency_id: visit.agency_id,
        date: visit.date,
        reason: visit.reason,
        status: visit.status,
        rating: visit.rating,
        agency_appearance: visit.agency_appearance,
        technical_aspects: visit.technical_aspects,
        human_aspects: visit.human_aspects,
        comercial_aspects: visit.comercial_aspects,
        summary: visit.summary,
        next_steps: visit.next_steps,
        internal_notes: visit.internal_notes ?? "",
        users: visit.users.map((u) => u.id),
        notification_users: visit.notification_users?.map((u) => u.id) ?? [],
      })
    }
  }, [visit])

  const handleSubmit = (values: AgencyVisitFormValues) => {
    if (isEdit) {
      updateMutation.mutate(
        { id: visit.id, values },
        {
          onSuccess: () => {
            notifications.show({ color: "green", title: t("common.success"), message: t("agencyVisits.updateSuccess") })
            onSuccess()
          },
          onError: (err) => {
            notifications.show({ color: "red", title: t("common.error"), message: err.message })
          },
        },
      )
    } else {
      createMutation.mutate(values, {
        onSuccess: () => {
          notifications.show({ color: "green", title: t("common.success"), message: t("agencyVisits.createSuccess") })
          onSuccess()
        },
        onError: (err) => {
          notifications.show({ color: "red", title: t("common.error"), message: err.message })
        },
      })
    }
  }

  const userOptions = (users ?? []).map((u: { id: number; fullName: string }) => ({
    value: String(u.id),
    label: u.fullName,
  }))

  const agencyOptions = (agencies ?? []).map((a: { id: number; name: string }) => ({
    value: String(a.id),
    label: a.name,
  }))

  const isPending = createMutation.isPending || updateMutation.isPending
  const handleFormSubmit = form.onSubmit(handleSubmit)
  useImperativeHandle(ref, () => ({ submit: () => handleFormSubmit() }))
  const dateValue = form.values.date ? new Date(form.values.date) : null

  return (
    <form onSubmit={handleFormSubmit}>
      <Stack>
        <FormSection title={t("agencyVisits.basicInfo")} accentColor="blue">
          <Group grow>
            <Select
              label={t("agencyVisits.agency")}
              placeholder={t("agencyVisits.selectAgency")}
              data={agencyOptions}
              searchable
              value={form.values.agency_id ? String(form.values.agency_id) : null}
              onChange={(val) => form.setFieldValue("agency_id", val ? Number(val) : 0)}
              error={form.errors.agency_id}
            />
            <DateInput
              label={t("agencyVisits.date")}
              placeholder=""
              valueFormat="YYYY-MM-DD"
              value={dateValue}
              onChange={(val) => form.setFieldValue("date", val ? val.toISOString().split("T")[0] : "")}
              error={form.errors.date}
            />
          </Group>
          <TextInput
            label={t("agencyVisits.reason")}
            placeholder={t("agencyVisits.reasonPlaceholder")}
            {...form.getInputProps("reason")}
          />
        </FormSection>

        <FormSection title={t("agencyVisits.evaluation")} accentColor="green">
          <Paper withBorder={false} bg="gray.0" p="md" radius="md" mb="md">
            <Group>
              <div style={{ flex: 1 }}>
                <Text size="sm" fw={500} mb={2}>
                  <Trans i18nKey="agencyVisits.rating" />
                </Text>
                <Text size="xs" c="dimmed">
                  Classifique a visita de 1 a 5 estrelas
                </Text>
              </div>
              <Rating
                value={form.values.rating ?? 0}
                onChange={(val) => form.setFieldValue("rating", val)}
                size="xl"
                count={5}
              />
            </Group>
          </Paper>
          <Group grow>
            <Textarea
              label={t("agencyVisits.agencyAppearance")}
              placeholder={t("agencyVisits.agencyAppearancePlaceholder")}
              minRows={4}
              autosize
              {...form.getInputProps("agency_appearance")}
            />
            <Textarea
              label={t("agencyVisits.technicalAspects")}
              placeholder={t("agencyVisits.technicalAspectsPlaceholder")}
              minRows={4}
              autosize
              {...form.getInputProps("technical_aspects")}
            />
          </Group>
          <Group grow>
            <Textarea
              label={t("agencyVisits.humanAspects")}
              placeholder={t("agencyVisits.humanAspectsPlaceholder")}
              minRows={4}
              autosize
              {...form.getInputProps("human_aspects")}
            />
            <Textarea
              label={t("agencyVisits.comercialAspects")}
              placeholder={t("agencyVisits.comercialAspectsPlaceholder")}
              minRows={4}
              autosize
              {...form.getInputProps("comercial_aspects")}
            />
          </Group>
          <Group grow>
            <Textarea
              label={t("agencyVisits.summary")}
              placeholder={t("agencyVisits.summaryPlaceholder")}
              minRows={4}
              autosize
              {...form.getInputProps("summary")}
            />
            <Textarea
              label={t("agencyVisits.nextSteps")}
              placeholder={t("agencyVisits.nextStepsPlaceholder")}
              minRows={4}
              autosize
              {...form.getInputProps("next_steps")}
            />
          </Group>
        </FormSection>

        <FormSection title={t("agencyVisits.team")} accentColor="violet">
          <MultiSelect
            label={t("agencyVisits.visitors")}
            placeholder={t("agencyVisits.selectVisitors")}
            data={userOptions}
            searchable
            clearable
            value={form.values.users.map(String)}
            onChange={(val) => form.setFieldValue("users", val.map(Number))}
            error={form.errors.users}
          />
          <MultiSelect
            label={t("agencyVisits.notificationUsers")}
            placeholder={t("agencyVisits.selectNotificationUsers")}
            data={userOptions}
            searchable
            clearable
            mt="md"
            value={form.values.notification_users.map(String)}
            onChange={(val) => form.setFieldValue("notification_users", val.map(Number))}
            error={form.errors.notification_users}
          />
        </FormSection>

        <FormSection title={t("agencyVisits.statusSection")} accentColor="orange">
          <Switch
            label={t("agencyVisits.markVisited")}
            checked={form.values.status === 10}
            onChange={(e) => form.setFieldValue("status", e.currentTarget.checked ? 10 : 5)}
            size="md"
          />
        </FormSection>

        <Paper withBorder p="md" style={{ borderLeft: "4px solid var(--mantine-color-red-5)" }}>
          <Text fw={600} size="sm" c="red" mb="md" tt="uppercase" lts="0.05em">
            <Trans i18nKey="agencyVisits.internalNotes" />
            <Text component="span" size="xs" c="dimmed" tt="none" ml="sm">
              (apenas visível para gestores)
            </Text>
          </Text>
          <Textarea
            placeholder={t("agencyVisits.internalNotesPlaceholder")}
            minRows={3}
            autosize
            {...form.getInputProps("internal_notes")}
          />
        </Paper>

      </Stack>
    </form>
  )
})

export default AgencyVisitFormContent
